import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Globe, CalendarClock } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Calendar } from "./ui/calendar";
import { es } from "date-fns/locale";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { dateLocale } from "../lib/meta";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function RescheduleDialog({ open, onOpenChange, appt, viewerTz, onDone }) {
  const { t, lang } = useLang();
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [availDates, setAvailDates] = useState(null);
  const tz = viewerTz || appt?.client_timezone;

  useEffect(() => { if (open) { setDate(null); setSlots([]); setSelected(null); } }, [open]);
  useEffect(() => {
    if (!open || !appt) { setAvailDates(null); return; }
    setAvailDates(null);
    api.get(`/psychologists/${appt.psychologist_id}/available-dates`, { params: { type_id: appt.type_id, modality: appt.modality } })
      .then((r) => setAvailDates(new Set(r.data.dates))).catch(() => setAvailDates(null));
  }, [open, appt]);
  useEffect(() => {
    if (!open || !date || !appt) { setSlots([]); return; }
    setLoading(true); setSelected(null);
    api.get(`/psychologists/${appt.psychologist_id}/slots`, { params: { date: iso(date), type_id: appt.type_id, modality: appt.modality, client_tz: tz } })
      .then((r) => setSlots(r.data)).catch(() => setSlots([])).finally(() => setLoading(false));
  }, [open, date, appt, tz]);

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.patch(`/appointments/${appt.id}/reschedule`, { start_utc: selected.start_utc, client_timezone: appt.client_timezone });
      toast.success(t("booking.rescheduled_ok"));
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("booking.reschedule_err"));
    } finally { setBusy(false); }
  };

  if (!appt) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="reschedule-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-terracotta" />{t("booking.reschedule_title")}</DialogTitle>
        <DialogDescription>{appt ? `${t("booking.current")}: ${new Date(appt.start_utc).toLocaleString(dateLocale(lang), { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: tz })}` : ""}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <Label>{t("booking.choose_new_time")}</Label>
          <div className="flex justify-center rounded-xl border border-border bg-white p-2">
            <Calendar mode="single" selected={date} onSelect={setDate} locale={lang === "es" ? es : undefined} disabled={(d) => d < new Date(new Date().toDateString()) || !availDates || !availDates.has(iso(d))} data-testid="reschedule-calendar" />
          </div>
          {availDates && <p className="text-xs text-muted-foreground">{t("booking.no_slots_hint")}</p>}
          {date && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground"><Globe className="h-3.5 w-3.5" />{tz}</p>
              {loading ? <div className="py-4 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
                : slots.length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground" data-testid="reschedule-no-slots">{t("booking.no_slots")}</p>
                  : <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="reschedule-slots">
                    {slots.map((s) => (
                      <button key={s.start_utc} onClick={() => setSelected(s)} data-testid={`reslot-${s.start_utc}`}
                        className={`rounded-lg border px-2 py-2 text-sm transition-colors ${selected?.start_utc === s.start_utc ? "border-terracotta bg-terracotta text-white" : "border-border hover:border-terracotta/50"}`}>
                        {s.client_local}
                      </button>
                    ))}
                  </div>}
            </div>
          )}
          <Button disabled={!selected || busy} onClick={submit} className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="reschedule-confirm">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("booking.confirm_new_time")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
